import {
    useState,
    useCallback,
    createContext,
    useContext,
    useMemo,
    ReactNode,
} from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import { MessageCircle, X } from 'lucide-react';

const CHATBASE_ID = 'bCnz_WehP_fSsLJ6_P_VJ';
const IFRAME_URL = `https://www.chatbase.co/chatbot-iframe/${CHATBASE_ID}`;

const PANEL_WIDTH = 380;
const PANEL_HEIGHT = 560;

/* ── Shared context so sidebar + FAB can both toggle ── */

interface ChatBotContextValue {
    open: boolean;
    toggle: () => void;
}

const ChatBotContext = createContext<ChatBotContextValue>({
    open: false,
    toggle: () => {},
});

export const useChatBot = () => useContext(ChatBotContext);

export function ChatBotProvider({ children }: { children: ReactNode }) {
    const [open, setOpen] = useState(false);
    const toggle = useCallback(() => setOpen(o => !o), []);
    const value = useMemo(() => ({ open, toggle }), [open, toggle]);
    return (
        <ChatBotContext.Provider value={value}>
            {children}
        </ChatBotContext.Provider>
    );
}

/* ── Floating panel + FAB ───────────────────────────── */

export default function ChatBotWidget() {
    const { open, toggle } = useChatBot();

    return (
        <>
            {/* ── Chat panel ─────────────────────────── */}
            {open && (
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: 88,
                        right: 24,
                        width: PANEL_WIDTH,
                        height: PANEL_HEIGHT,
                        borderRadius: '12px',
                        overflow: 'hidden',
                        boxShadow:
                            '0 10px 25px rgba(0,0,0,0.15), 0 4px 10px rgba(0,0,0,0.08)',
                        border: '1px solid #e2e8f0',
                        zIndex: 1300,
                        bgcolor: '#fff',
                        display: 'flex',
                        flexDirection: 'column',
                        /* mobile: go full-width with some margin */
                        '@media (max-width: 480px)': {
                            width: 'calc(100vw - 32px)',
                            height: 'calc(100vh - 140px)',
                            right: 16,
                            bottom: 80,
                        },
                    }}
                >
                    {/* Header bar */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 1.5,
                            py: 1,
                            borderBottom: '1px solid #e2e8f0',
                            bgcolor: '#f8fafc',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                color: '#0f172a',
                            }}
                        >
                            <MessageCircle size={16} color="#10b981" />
                            Hail Manual Assistant
                        </Box>
                        <IconButton
                            size="small"
                            onClick={toggle}
                            sx={{ color: '#64748b' }}
                        >
                            <X size={16} />
                        </IconButton>
                    </Box>

                    {/* ChatBase iframe */}
                    <Box
                        component="iframe"
                        src={IFRAME_URL}
                        sx={{
                            flex: 1,
                            width: '100%',
                            border: 'none',
                        }}
                        title="Hail Manual Assistant"
                    />
                </Box>
            )}

            {/* ── Floating action button ─────────────── */}
            <Tooltip
                title={open ? 'Close assistant' : 'Ask about hail manuals'}
                placement="left"
            >
                <IconButton
                    onClick={toggle}
                    sx={{
                        position: 'fixed',
                        bottom: 24,
                        right: 24,
                        width: 52,
                        height: 52,
                        bgcolor: open ? '#475569' : '#10b981',
                        color: '#fff',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        zIndex: 1300,
                        '&:hover': {
                            bgcolor: open ? '#334155' : '#059669',
                        },
                        transition: 'background-color 0.2s, transform 0.2s',
                        '&:active': {
                            transform: 'scale(0.95)',
                        },
                    }}
                >
                    {open ? <X size={22} /> : <MessageCircle size={22} />}
                </IconButton>
            </Tooltip>
        </>
    );
}
